/**
 * CreateUserModal — migrated to unified modal system.
 * 6 fields: firstName, lastName, email, password, role, phone.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UserIcon, MailIcon, LockIcon, PhoneIcon, ShieldIcon, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, FormField, useVinylSubmit, MODAL_COLORS } from '@/components/modal';
import { useCreateUser } from '../hooks';
import { useActiveRoles } from '@/features/admin/hooks';
import { HospitalAssignmentField } from './HospitalAssignmentField';
import { localizedName } from '@/utils/localized';
import type { CreateUserData } from '../types';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateUserModal({
  isOpen,
  onClose,
  onSuccess,
}: CreateUserModalProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const createUser = useCreateUser();
  const { data: activeRoles } = useActiveRoles();
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { isAnimating, submitWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: 'NEW USER',
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('EMP');
  const [phone, setPhone] = useState('');
  const [hospitalIds, setHospitalIds] = useState<string[]>([]);
  const [primaryHospitalId, setPrimaryHospitalId] = useState<string | undefined>();
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPassword('');
      setRole('EMP');
      setPhone('');
      setHospitalIds([]);
      setPrimaryHospitalId(undefined);
      setError('');
    }
  }, [isOpen]);

  const focusField = (id: string) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
    });
  };

  const validateForm = (): boolean => {
    setError('');
    if (!firstName.trim()) {
      setError(isHebrew ? '\u05E0\u05D0 \u05DC\u05D4\u05D6\u05D9\u05DF \u05E9\u05DD \u05E4\u05E8\u05D8\u05D9' : 'First name is required');
      focusField('cu-firstName');
      return false;
    }
    if (!lastName.trim()) {
      setError(isHebrew ? '\u05E0\u05D0 \u05DC\u05D4\u05D6\u05D9\u05DF \u05E9\u05DD \u05DE\u05E9\u05E4\u05D7\u05D4' : 'Last name is required');
      focusField('cu-lastName');
      return false;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(isHebrew ? '\u05E0\u05D0 \u05DC\u05D4\u05D6\u05D9\u05DF \u05D3\u05D5\u05D0"\u05DC \u05EA\u05E7\u05D9\u05DF' : 'Valid email is required');
      focusField('cu-email');
      return false;
    }
    if (password.length < 8) {
      setError(isHebrew ? '\u05E1\u05D9\u05E1\u05DE\u05D4 \u05D7\u05D9\u05D9\u05D1\u05EA \u05DC\u05D4\u05D9\u05D5\u05EA 8 \u05EA\u05D5\u05D5\u05D9\u05DD \u05DC\u05E4\u05D7\u05D5\u05EA' : 'Password must be at least 8 characters');
      focusField('cu-password');
      return false;
    }
    return true;
  };

  const isFormValid = useMemo(() => {
    if (!firstName.trim() || !lastName.trim()) return false;
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
    if (password.length < 8) return false;
    return true;
  }, [firstName, lastName, email, password]);

  const handleSubmit = () => {
    const payload: CreateUserData = {
      email: email.trim(),
      password,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      role,
      phone: phone.trim() || undefined,
      hospitalIds: hospitalIds.length > 0 ? hospitalIds : undefined,
      primaryHospitalId: primaryHospitalId || undefined,
    };

    submitWithAnimation({
      validate: validateForm,
      mutate: () => createUser.mutateAsync(payload),
      onSuccess: () => {
        toast.success(t('users.created'));
        onSuccess();
        onClose();
      },
      onError: (err: any) => {
        setError(err.response?.data?.error || err.response?.data?.message || t('common.error'));
      },
    });
  };

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('users.createUser')}
      isPending={createUser.isPending}
      isAnimating={isAnimating}
      onSubmit={handleSubmit}
      onValidate={validateForm}
      isFormValid={isFormValid}
      modalContentRef={modalContentRef}
      vinylLabel="NEW USER"
      error={error}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<UserIcon className="h-4 w-4" />} label={t('users.firstName')} required>
            <input
              id="cu-firstName"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
          <FormField icon={<UserIcon className="h-4 w-4" />} label={t('users.lastName')} required>
            <input
              id="cu-lastName"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>
        <FormField icon={<MailIcon className="h-4 w-4" />} label={t('auth.email')} required>
          <input
            id="cu-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>
        <FormField icon={<LockIcon className="h-4 w-4" />} label={t('auth.password')} required>
          <input
            id="cu-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-2">
          <FormField icon={<ShieldIcon className="h-4 w-4" />} label={t('users.role')} required>
            <select
              id="cu-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2 cursor-pointer"
              style={inputStyle}
            >
              {activeRoles
                ?.filter((r) => !r.isSystem)
                .map((r) => (
                  <option key={r.code} value={r.code}>
                    {localizedName(r, i18n.language)}
                  </option>
                )) ?? (
                <>
                  <option value="EMP">{t('users.roles.EMPLOYEE')}</option>
                  <option value="MANAGER">{t('users.roles.MANAGER')}</option>
                  <option value="ADMIN">{t('users.roles.ADMIN')}</option>
                </>
              )}
            </select>
          </FormField>
          <FormField icon={<PhoneIcon className="h-4 w-4" />} label={t('users.phone')}>
            <input
              id="cu-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        </div>
        <FormField icon={<Building2 className="h-4 w-4" />} label={t('users.assignHospitals')}>
          <HospitalAssignmentField
            selectedIds={hospitalIds}
            primaryId={primaryHospitalId}
            onChange={(ids, pId) => {
              setHospitalIds(ids);
              setPrimaryHospitalId(pId);
            }}
          />
        </FormField>
      </div>
    </FormModal>
  );
}
